import { Component, h, Prop, Element, Event, EventEmitter, State, Listen } from '@stencil/core';
import { CmsLinkConfig, Logger } from '../../../global/_types';
import { CmsLatLon } from '../../../global/_googleMaps';
import { isSmallerThanViewport } from '../../../global/_utils';
import { breakpoints } from '../../../global/_constants';
import { CmsDataEvent, CmsDataLocation } from '../../../global/_data';

@Component({
  tag: 'cms-filter-content-event',
  styleUrl: 'cms-filter-content-event.scss',
  shadow: true,
})

export class CmsFilterContentEvent {
  @Element() host: HTMLElement;

  @Prop() config: string | CmsFilterContentEventConfig;
  @Prop() customConfig: string | CmsCustomConfigEvents;
  @Prop() showMap: boolean;
  @Prop() rowIndex: number;
  @Prop() debug: boolean = false;

  @State() isMobile: boolean = false;

  /* Config */
  private options: CmsFilterContentEventConfig;
  private customOptions: CmsCustomConfigEvents;

  private static defaultValues = {
    labels: {
      price: 'free',
      dateDelimiter: ' - ',
      timeDelimiter: ' - ',
      timeSuffix: ''
    }
  }

  private logger: Logger;

  // Lifecycle
  componentWillLoad() {
    this.logger = new Logger(this.debug, 'CmsFilterContentEvent');
  }

  // Force content update on each render call
  componentWillRender() {
    this.options = typeof (this.config) === 'string'
                   ? JSON.parse(this.config) as CmsFilterContentEventConfig
                   : this.config;

    this.customOptions = {
      ...CmsFilterContentEvent.defaultValues,
      ...(typeof (this.customConfig) === 'string'
        ? JSON.parse(this.customConfig) as CmsCustomConfigEvents
        : this.customConfig)
    };

    const bookingLink = this.options.eventNameMapping?.bookingLink ? this.options.eventNameMapping.bookingLink : this.customOptions.bookingLink;
    if (bookingLink && this.customOptions.labels.bookingLink) {
      this.options.button = {
        link: bookingLink.concat(this.options.id).concat('&refl=').concat(this.customOptions.hashedLink),
        label: this.customOptions.labels.bookingLink,
        button: {
          layout: 'redrat',
          width: '240px',
        },
      } as CmsLinkConfig;
    }

    if (!this.options.isOnline && this.options.location && this.customOptions.mapsLink && this.customOptions.labels.mapsLink) {
      const geolocation = this.options.location?.location as CmsLatLon;
      this.options.mapsLink = {
        link: this.customOptions.mapsLink.concat(geolocation?.lat, ',', geolocation?.lon),
        label: this.customOptions.labels.mapsLink,
        target: '_blank'
      } as CmsLinkConfig;
    }
  }

  componentDidLoad() {
    this.logger.trace('CmsFilterContentEvent loaded');
  }

  /**
   * Process style changes on viewport resize
   * Resize EventHandler
   */
  @Listen('resize', {target: 'window'})
  updateViewport() {
    this.isMobile = isSmallerThanViewport(breakpoints.ml);
  }

  // Render
  render() {
    const image = this.options.eventNameMapping?.picture ? this.options.eventNameMapping.picture : this.customOptions.fallbackImage;
    const imageMobile = this.options.eventNameMapping?.pictureMobile ? this.options.eventNameMapping.pictureMobile : this.customOptions.fallbackImageMobile;
    const imageSrc = this.isMobile ? imageMobile : image;
    const rowStyle = { backgroundColor: this.customOptions.color ? this.customOptions.color : null };
    const hasEndDate = this.options.enddate && this.options.startdate != this.options.enddate;
    const weekday = !hasEndDate && this.customOptions.showWeekday && this.options.weekday
                    ? `${this.options.weekday}${this.customOptions.labels.weekdayDelimiter ? this.customOptions.labels.weekdayDelimiter : '' } `
                    : '';

    return (
      <div class={`row ${this.showMap ? 'show-map' : ''} ${this.rowIndex==0 ? 'first-row' : ''}`} style={rowStyle}>
        <div class={`image`}>
          {imageSrc && imageSrc!='' ?
            <img src={imageSrc} role="presentation" width="560" height="560"/>
          : '' }
        </div>
        <div class={`content`}>
          {
            this.options.free
              ? { ...this.customOptions.labels?.priceFree ? <div class='price'>{this.customOptions.labels.priceFree}</div> : '' }
              : ''
          }
          <div class={`description`}>
            {this.options.type ?
              <div class='type'>{this.options.type}</div>
            : ''}
            {this.options.eventNameMapping?.headline ?
              <div class='headline'>{this.options.eventNameMapping.headline}</div>
            : ''}
            {this.options.specialtype ?
              <div class="text">{this.options.specialtype}</div>
              : ''}
          </div>
          <div class="info">
            {this.options.startdate || this.options.starttime ?
              <div class="date">
                {this.options.startdate ?
                  <span>
                    {weekday}
                    {this.options.startdate}
                    {hasEndDate ? ` ${this.customOptions.labels.dateDelimiter}  ${this.options.enddate}` : ''}
                  </span>
                : ''}
                {this.options.starttime ?
                  <span>
                    {this.options.starttime}
                    {this.options.starttime != this.options.endtime ? ` ${this.customOptions.labels.timeDelimiter} ${this.options.endtime}` : ''}
                    {this.customOptions.labels.timeSuffix ? ` ${this.customOptions.labels.timeSuffix}` : ''}
                  </span>
                : ''}
              </div>
            : ''}
            {this.options.language ?
              <div class="language"><span>{this.options.language}</span></div>
              : ''}
            {this.options.subtype ?
              <div class="sub-type"><span>{this.options.subtype}</span></div>
              : ''}
          </div>
          <div class="info">
            {this.options.location ?
              <div class='location' onClick={this.showMarker.bind(this)}>
                {this.options.location.name ? <p class='bold'>{this.options.location.name}</p> : ''}
                {this.options.location.address ? <p>{this.options.location.address}</p> : ''}
                {this.options.location.city ? <p>{this.options.location.zipcode} {this.options.location.city}</p> : ''}
                {this.options.location.countryName ? <p>{this.options.location.countryName}</p> : ''}
              </div>
              : this.options.isOnline ? <div class='location'><p class='bold'>{this.customOptions.labels.onlineEvent}</p></div> : ''
            }
          </div>
          {/* will be used in dealers:
          { !this.options.isOnline && this.options.mapsLink ?
            <div class="offline">
              <cms-link-or-button debug={this.debug} config={this.options.mapsLink} />
            </div>
          : ''}
          */}
        </div>
        <div class="booking">
          {
            this.options.button
              ? <cms-link-or-button debug={this.debug} config={this.options.button} />
              : ''
          }
        </div>
        { /* to use old price label, add following line to else part:
          { ...this.customOptions.labels?.price ? <div class='price'>{this.customOptions.labels.price}</div> : '' }
         */ }
      </div>
    );
  }

  // Events
  @Event({
    eventName: 'showMarker',
    composed: true,
    cancelable: true,
    bubbles: true,
  })
  showMarkerEvent: EventEmitter;

  private showMarker() {
    this.showMarkerEvent.emit({ id: this.options.id });
  }
}

export interface CmsFilterContentEventConfig extends CmsDataEvent {
  language?: string,
  distance: number,
  eventNameMapping: CmsEventNameMapping,
  button: CmsLinkConfig,
  mapsLink: CmsLinkConfig,
  location: CmsDataLocation,
  city: string,
  country: string,
  countryName: string
}

export interface CmsCustomConfigEvents {
  partnerCountries: {},
  showWeekday?: boolean,
  mapsLink: string,
  bookingLink: string,
  fallbackImage: string,
  fallbackImageMobile: string,
  hashedLink: string,
  labels: {
    mapsLink: string,
    bookingLink: string,
    price: string,
    priceFree: string,
    weekdayDelimiter: string,
    dateDelimiter: string,
    timeDelimiter: string,
    timeSuffix: string,
    onlineEvent: string
  },
  color?: string
}

interface CmsEventNameMapping {
  id: number,
  name: string,
  headline: string,
  text: string,
  description: string,
  picture: string,
  pictureMobile: string,
  contentLink: string,
  bookingLink: string,
  categories: string[]
}
